// Las cápsulas de autocapacitación fuera del panel, para mandarlas por correo:
//   tabularis.cl/aprende        → todas, por ruta, con "Copiar enlace"
//   tabularis.cl/aprende/0.4    → una sola, con las demás de su ruta debajo
//
// No está enlazada desde la portada ni se indexa (utils/seo la deja en
// noindex): es para quien recibe el enlace. Antes los enlaces se armaban
// desde iCloud. Los videos son los mismos de /capsulas/ que ve el panel, así
// que regrabar una cápsula no pide desplegar esto.
import React, { useEffect, useMemo, useState } from 'react';
import { cargarCatalogo, duracionTexto, type Capsula, type Catalogo } from '../utils/capsulas';
import { registrarVisita } from '../utils/visitas';
import { LogoLockup } from '../components/Icons';
import { COLOR, WhatsAppFlotante } from './LandingApp';

const enlace = (num: string) => `${window.location.origin}/aprende/${num}`;

const AprendePublico: React.FC = () => {
  const [cat, setCat] = useState<Catalogo | null>(null);
  const [error, setError] = useState(false);
  // /aprende/0.4 o /aprende/0.4/lo-que-sea: lo que importa es el número.
  const num = window.location.pathname.split('/')[2] || '';

  useEffect(() => { cargarCatalogo().then(setCat).catch(() => setError(true)); }, []);
  useEffect(() => { registrarVisita('aprende'); }, []);

  const actual = cat?.capsulas.find((c) => c.num === num) || null;
  useEffect(() => {
    document.title = actual ? `${actual.titulo} · Aprende Tabularis` : 'Aprende Tabularis';
  }, [actual]);

  return (
    <div style={{ minHeight: '100vh', background: '#F6F8FC', color: COLOR.navy, fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
      <header style={{ background: '#fff', borderBottom: `1px solid ${COLOR.line}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <a href="/" aria-label="Tabularis"><LogoLockup height={30} /></a>
          {num && <a href="/aprende" style={{ color: COLOR.blue2, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Todos los videos</a>}
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 72px' }}>
        {error && <p style={{ color: COLOR.muted }}>Los videos no están disponibles en este momento. Intenta de nuevo más tarde.</p>}
        {!cat && !error && <p style={{ color: COLOR.muted }}>Cargando…</p>}
        {cat && num && !actual && (
          <p style={{ background: '#fff', border: `1px solid ${COLOR.line}`, borderRadius: 10, padding: '12px 16px' }}>
            Ese video ya no está. Abajo están todos los que hay.
          </p>
        )}
        {cat && actual && <Video cat={cat} c={actual} />}
        {cat && !actual && <Indice cat={cat} />}
      </main>
      <WhatsAppFlotante />
    </div>
  );
};

/** Una cápsula, y debajo las demás de su ruta. */
const Video: React.FC<{ cat: Catalogo; c: Capsula }> = ({ cat, c }) => {
  const ruta = cat.rutas[String(c.ruta)];
  const otras = cat.capsulas.filter((x) => x.ruta === c.ruta && x.num !== c.num);
  return (
    <>
      <div style={{ fontSize: 13, color: COLOR.muted, fontWeight: 600 }}>
        {ruta?.titulo ? `${ruta.titulo} · ` : ''}{duracionTexto(c.duracion)}
      </div>
      <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(24px, 3.2vw, 34px)', margin: '4px 0 14px', lineHeight: 1.2 }}>{c.titulo}</h1>
      <div style={{ borderRadius: 14, overflow: 'hidden', background: '#000', boxShadow: '0 18px 50px rgba(13,27,53,0.18)' }}>
        {/* Los subtítulos ya vienen en el video. */}
        <video key={c.video} src={c.video} poster={c.poster} controls playsInline preload="metadata"
          controlsList="nodownload" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}
          style={{ width: '100%', display: 'block', aspectRatio: '16 / 9' }} />
      </div>
      {c.resumen && <p style={{ fontSize: 15.5, lineHeight: 1.55, color: '#33435E', margin: '14px 0 0' }}>{c.resumen.replace(/[.\s]+$/, '')}.</p>}
      {otras.length > 0 && (
        <section style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: 18, margin: '0 0 12px' }}>Más de {ruta?.titulo || 'esta ruta'}</h2>
          <Grilla caps={otras} />
        </section>
      )}
    </>
  );
};

/** Todas, por ruta. */
const Indice: React.FC<{ cat: Catalogo }> = ({ cat }) => {
  const rutas = useMemo(() => {
    const m = new Map<number, Capsula[]>();
    cat.capsulas.forEach((c) => m.set(c.ruta, [...(m.get(c.ruta) || []), c]));
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [cat]);
  return (
    <>
      <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(26px, 3.4vw, 36px)', margin: '0 0 6px' }}>Aprende Tabularis</h1>
      <p style={{ color: COLOR.muted, margin: '0 0 28px', fontSize: 15 }}>Videos de 1 a 3 minutos, una tarea cada uno.</p>
      {rutas.map(([n, caps]) => (
        <section key={n} style={{ marginBottom: 36 }}>
          <h2 style={{ fontSize: 19, margin: 0 }}>{cat.rutas[String(n)]?.titulo || `Ruta ${n}`}</h2>
          {cat.rutas[String(n)]?.descripcion && <p style={{ color: COLOR.muted, margin: '2px 0 12px', fontSize: 14 }}>{cat.rutas[String(n)].descripcion}</p>}
          <Grilla caps={caps} copiar />
        </section>
      ))}
    </>
  );
};

const Grilla: React.FC<{ caps: Capsula[]; copiar?: boolean }> = ({ caps, copiar }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: 14 }}>
    {caps.map((c) => <Tarjeta key={c.num} c={c} copiar={copiar} />)}
  </div>
);

/** Tarjeta que lleva al video. Con "Copiar enlace" para pegarlo en un correo. */
const Tarjeta: React.FC<{ c: Capsula; copiar?: boolean }> = ({ c, copiar }) => {
  const [copiado, setCopiado] = useState(false);
  const copiarEnlace = () => {
    navigator.clipboard?.writeText(enlace(c.num)).then(() => {
      setCopiado(true); setTimeout(() => setCopiado(false), 1800);
    }).catch(() => window.prompt('Copia el enlace:', enlace(c.num)));
  };
  return (
    <div style={{ background: '#fff', border: `1px solid ${COLOR.line}`, borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <a href={`/aprende/${c.num}`} style={{ color: 'inherit', textDecoration: 'none', display: 'block' }}>
        <span style={{ position: 'relative', display: 'block', aspectRatio: '16 / 9', background: '#000' }}>
          <img src={c.poster} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <span style={{ position: 'absolute', right: 6, bottom: 6, background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 11, borderRadius: 4, padding: '1px 6px' }}>{duracionTexto(c.duracion)}</span>
        </span>
        <span style={{ display: 'block', padding: '10px 12px 4px', fontWeight: 600, fontSize: 14.5, lineHeight: 1.3 }}>{c.titulo}</span>
      </a>
      {copiar && (
        <button onClick={copiarEnlace}
          style={{ margin: 'auto 12px 12px', alignSelf: 'flex-start', background: 'none', border: `1px solid ${COLOR.line}`, borderRadius: 6, padding: '4px 10px', fontSize: 12.5, color: copiado ? '#15803D' : COLOR.blue2, cursor: 'pointer' }}>
          {copiado ? 'Enlace copiado ✓' : 'Copiar enlace'}
        </button>
      )}
    </div>
  );
};

export default AprendePublico;
